import React from 'react'
import {
  Box,
  Checkbox,
  type CheckboxProps,
  Radio,
  type RadioProps,
  Switch,
  type SwitchProps,
  type SxProps,
  type Theme,
  Typography,
  type TypographyProps,
  useTheme,
} from '@mui/material'

import { borderRadius, borders, lightTokens, spacing, transitions } from '~tokens/internal'

type ControlType = 'switch' | 'radio' | 'checkbox'

interface BaseSelectionCardProps {
  icon?: React.ReactNode
  /** 'side' places the icon to the left of the text (default). 'top' places it above. */
  iconLayout?: 'side' | 'top'
  label?: string
  labelVariant?: TypographyProps['variant']
  description?: string
  cardSx?: SxProps<Theme>
  controlType?: ControlType
  disabled?: boolean
  controlPosition?: 'start' | 'end'
  children?: React.ReactNode
}

export interface SelectionCardSwitchProps extends BaseSelectionCardProps {
  controlType?: 'switch'
  controlProps?: Omit<SwitchProps, 'disabled'>
}

export interface SelectionCardRadioProps extends BaseSelectionCardProps {
  controlType: 'radio'
  controlProps?: Omit<RadioProps, 'disabled'>
}

export interface SelectionCardCheckboxProps extends BaseSelectionCardProps {
  controlType: 'checkbox'
  controlProps?: Omit<CheckboxProps, 'disabled'>
}

export type SelectionCardProps =
  | SelectionCardSwitchProps
  | SelectionCardRadioProps
  | SelectionCardCheckboxProps

const SelectionCard: React.FC<SelectionCardProps> = ({
  icon,
  iconLayout = 'side',
  label,
  labelVariant = 'subtitle1',
  description,
  cardSx,
  controlType = 'switch',
  controlProps,
  disabled,
  controlPosition = 'end',
  children,
}) => {
  const theme = useTheme()
  const isSelected = Boolean(
    controlProps?.checked !== undefined ? controlProps.checked : controlProps?.defaultChecked,
  )

  const renderControl = (): React.ReactNode => {
    switch (controlType) {
      case 'radio':
        return <Radio disabled={disabled} {...(controlProps as RadioProps)} />
      case 'checkbox':
        return <Checkbox disabled={disabled} {...(controlProps as CheckboxProps)} />
      case 'switch':
      default:
        return <Switch disabled={disabled} {...(controlProps as SwitchProps)} />
    }
  }

  const renderIcon = (): React.ReactNode => {
    if (!icon) return null

    const iconColor = isSelected ? theme.palette.primary.main : theme.palette.text.secondary
    return (
      <Box
        sx={{
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          transition: `color ${transitions.duration.fast} ${transitions.easing.easeOut}`,
        }}
      >
        {icon}
      </Box>
    )
  }

  const cardBase = {
    p: spacing.xs,
    border: `${borders.width.thin} solid`,
    borderColor: isSelected ? 'primary.main' : 'divider',
    backgroundColor: isSelected ? lightTokens.primary.states.selected : 'transparent',
    borderRadius: borderRadius.sm,
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? 'default' : 'pointer',
    transition: `all ${transitions.duration.fast} ${transitions.easing.easeOut}`,
    '&:hover': {
      borderColor: disabled ? 'divider' : 'primary.main',
      backgroundColor: disabled ? 'transparent' : 'action.hover',
    },
    ...cardSx,
  }

  if (iconLayout === 'top') {
    return (
      <Box
        component='label'
        sx={{ display: 'flex', flexDirection: 'column', gap: spacing.xxs, ...cardBase }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {renderIcon()}
          {renderControl()}
        </Box>
        {label && <Typography variant={labelVariant}>{label}</Typography>}
        {description && (
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <Box component='label' sx={{ display: 'flex', gap: spacing.xs, ...cardBase }}>
      {controlPosition === 'start' && renderControl()}
      {renderIcon()}
      <Box sx={{ flex: 1 }}>
        {label && <Typography variant={labelVariant}>{label}</Typography>}
        {description && (
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
            }}
          >
            {description}
          </Typography>
        )}
        {children && <Box sx={{ mt: spacing.xs }}>{children}</Box>}
      </Box>
      {controlPosition === 'end' && renderControl()}
    </Box>
  )
}

export default SelectionCard
